import type { SearchResultItem, ContentCardItem, ClassificationResults, ContentMetadata } from '@content-search/domain';
import { toContentType } from '@content-search/domain';
import { getContentDisplayText } from '@core/utils';

import { calculatePolicyResult } from './calculatePolicyResult';

/**
 * Creates a ContentCardItem from a SearchResultItem.
 * Pure data transformation — no React or component-layer dependencies.
 */
// eslint-disable-next-line complexity
export function createContentCardItem(item: SearchResultItem, index: number): ContentCardItem {
  const itemId = item.id ?? `item-${index}`;

  // Extract data from the camelCase classificationResults and contentMetadata (now properly transformed by BFF)
  const classificationResults = item.classificationResults as ClassificationResults;
  const contentMetadata = item.contentMetadata as ContentMetadata;

  // Extract nested data from contentMetadata (now camelCase from BFF)
  const metadata = contentMetadata?.metadata || {};
  const subjectOwner = contentMetadata?.subjectOwner || {};
  const customValues = contentMetadata?.customValues || {};

  // Map labels from classificationResults if available, otherwise use legacy labels
  const labels = classificationResults?.labels
    ? classificationResults.labels.map(label => ({
        name: label.labelName,
        severity: typeof label.severity === 'string' ? parseInt(label.severity, 10) || 0 : label.severity,
        label_num_map: label.labelNumMap,
        label_short_name: label.labelShortName,
        label_id: label.labelId,
        content_type: label.contentType,
        score: label.score,
      }))
    : (item.labels ?? []);

  return {
    id: itemId,
    subjectId: contentMetadata?.subjectId ?? itemId,
    type: toContentType(item.contentType),
    text: getContentDisplayText(classificationResults?.transcript, contentMetadata?.contentUrl, contentMetadata?.audioFileUrl),
    topic: String(metadata?.world ?? 'Unknown'),
    topicName: item.topicName,
    language: classificationResults?.languageDetected,
    timestamp: item.createdAt,
    subjectOwner: {
      type: subjectOwner?.type ?? 'Unknown',
      userId: subjectOwner?.id ?? 'Unknown',
      username: typeof subjectOwner?.additionalInfo?.username === 'string' ? subjectOwner.additionalInfo.username : 'Unknown User',
    },
    audioFileUrl: contentMetadata?.audioFileUrl,
    contentUrl: contentMetadata?.contentUrl,
    eventType: item.eventType,
    contentType: item.contentType,
    isSampled: item.isSampled,
    metadata: {
      ...Object.fromEntries(Object.entries(metadata).map(([key, value]) => [key, String(value ?? '')])),
    },
    customFields: Object.fromEntries(Object.entries(customValues).map(([key, value]) => [key, typeof value === 'number' ? value : String(value ?? '')])),
    labels: labels.map(label => ({
      name: label.name,
      severity: typeof label.severity === 'string' ? parseInt(label.severity, 10) || 0 : label.severity,
      label_num_map: 'label_num_map' in label ? (label.label_num_map as number) : undefined,
      label_short_name: 'label_short_name' in label ? (label.label_short_name as string) : undefined,
      label_id: 'label_id' in label ? (label.label_id as string) : undefined,
      content_type: 'content_type' in label ? (label.content_type as string) : undefined,
      score: 'score' in label ? (label.score as number) : undefined,
    })),
    samplingStatus: classificationResults || item.isSampled ? 'sampled' : 'not_sampled',
    classificationResult: classificationResults?.classificationType === 'harmful_content' ? 'rejected' : 'allowed',
    // Add classification metadata if available (keeping snake_case as expected by ContentCardItem)
    classificationMetadata: classificationResults
      ? {
          processing_time_ms: classificationResults.processingTimeMs ?? 0,
          confidence_score: classificationResults.confidenceScore ?? 0,
          execution_time_ms: classificationResults.executionTimeMs,
        }
      : undefined,
    // Calculate policy result from policies and nonTriggeredPolicies
    policyResult: calculatePolicyResult(classificationResults?.policies, classificationResults?.nonTriggeredPolicies),
    // Store raw policies with label mappings for per-label policy display
    classificationPolicies: classificationResults?.policies,
    classificationNonTriggeredPolicies: classificationResults?.nonTriggeredPolicies,
  };
}
